import api from "./api";

export const externalLinkProps = {
  target: "_blank",
  rel: "noopener noreferrer",
};

const unwrap = (response) => response.data;
const TRAVEL_LINKS_WRITE_ENDPOINT = "/api/travel-links";
const PUBLIC_API_TIMEOUT_MS = 3500;
const SUBMIT_API_TIMEOUT_MS = 8000;

const DEFAULT_TRAVEL_LINKS = {
  ibiza: [
    {
      id: "ibiza-hotel-deals-tiddly-1",
      destination: "Ibiza",
      category: "hotel",
      partner_name: "lastminute.com",
      display_title: "THB Naeco Ibiza - Adults Only",
      display_subtitle: "4-star hotel · 9.0 Excellent · 235 reviews · San Antonio de Portmany",
      image_url:
        "/assets/hotels/thb-naeco-ibiza.jpg",
      image_alt: "THB Naeco Ibiza pool and sun loungers",
      awin_url: "https://tidd.ly/4wjWdRt",
      active: true,
      sort_order: 1,
    },
    {
      id: "ibiza-flight-hotel-packages-tiddly-2",
      destination: "Ibiza",
      category: "hotel",
      partner_name: "lastminute.com",
      display_title: "Hotel Vibra Mare Nostrum",
      display_subtitle: "3-star hotel · 8.6 Very Good · 9,365 reviews · Playa d'en Bossa",
      image_url: "/assets/community/zzz2333333.avif?v=20260817-2",
      image_alt: "Hotel Vibra Mare Nostrum Ibiza package image",
      awin_url: "https://tidd.ly/49c5amb",
      active: true,
      sort_order: 2,
    },
    {
      id: "ibiza-concierge-build-trip",
      destination: "Ibiza",
      category: "concierge",
      partner_name: "HLT Concierge",
      display_title: "Build the full Ibiza trip with HLT Concierge",
      display_subtitle:
        "Hotels, villas, VIP tables, after parties, villa parties, yacht days and transfers",
      awin_url: "/concierge#concierge-request-form",
      active: true,
      sort_order: 3,
    },
  ],
};

const normaliseKey = (value) => String(value || "").trim().toLowerCase();

const looksLikeTravelLink = (item) =>
  Boolean(
    item &&
      (item.awin_url ||
        item.awinUrl ||
        item.display_title ||
        item.displayTitle ||
        item.partner_name ||
        item.partnerName)
  );

const fallbackTravelLinks = (params = {}) => {
  const destination = normaliseKey(params.destination);
  const category = normaliseKey(params.category);
  const links = (DEFAULT_TRAVEL_LINKS[destination] || []).filter((link) =>
    category ? normaliseKey(link.category) === category : true
  );

  return { links, items: links, content: links };
};

const unwrapTravelLinks = (params = {}) => (response) => {
  const data = response.data;
  const fallback = fallbackTravelLinks(params);

  if (Array.isArray(data)) {
    return data.some(looksLikeTravelLink) ? data : fallback;
  }

  const list = data?.links || data?.items || data?.content;
  if (Array.isArray(list)) {
    return list.some(looksLikeTravelLink) ? data : fallback;
  }

  return fallback;
};

export function withAwinClickRef(url, affiliateCode) {
  if (!affiliateCode) return url;

  try {
    const parsed = new URL(url);
    parsed.searchParams.set("clickref", affiliateCode);
    return parsed.toString();
  } catch {
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}clickref=${encodeURIComponent(affiliateCode)}`;
  }
}

export const externalEventsApi = {
  list: (params = {}) =>
    api.get("/api/external-events", { params, timeout: PUBLIC_API_TIMEOUT_MS }).then(unwrap),
  create: (payload) => api.post("/api/external-events", payload).then(unwrap),
  update: (id, payload) => api.put(`/api/external-events/${id}`, payload).then(unwrap),
  remove: (id) => api.delete(`/api/external-events/${id}`).then(unwrap),
};

export const awinApi = {
  listLinks: (params = {}) =>
    api
      .get("/api/external-events", {
        params: { ...params, travelLinks: true },
        timeout: PUBLIC_API_TIMEOUT_MS,
      })
      .then(unwrapTravelLinks(params))
      .catch(() => fallbackTravelLinks(params)),
  createLink: (payload) => api.post(TRAVEL_LINKS_WRITE_ENDPOINT, payload).then(unwrap),
  updateLink: (id, payload) => api.put(`${TRAVEL_LINKS_WRITE_ENDPOINT}/${id}`, payload).then(unwrap),
  removeLink: (id) => api.delete(`${TRAVEL_LINKS_WRITE_ENDPOINT}/${id}`).then(unwrap),
  listSales: (params = {}) => api.get("/api/awin/sales", { params }).then(unwrap),
  exportSalesUrl: (params = {}) => {
    const search = new URLSearchParams(params).toString();
    return `/api/awin/sales/export${search ? `?${search}` : ""}`;
  },
};

export const conciergeApi = {
  submit: (payload) => api.post("/api/concierge/submit", payload, { timeout: SUBMIT_API_TIMEOUT_MS }).then(unwrap),
  gdbSignup: (payload) =>
    api.post("/api/auth/gdb-signup", payload, { timeout: SUBMIT_API_TIMEOUT_MS }).then(unwrap),
  listRequests: (params = {}) => api.get("/api/concierge/requests", { params }).then(unwrap),
  getRequest: (id) => api.get(`/api/concierge/requests/${id}`).then(unwrap),
  updateRequest: (id, payload) => api.put(`/api/concierge/requests/${id}`, payload).then(unwrap),
  addNote: (id, payload) => api.post(`/api/concierge/requests/${id}/notes`, payload).then(unwrap),
  getStats: () => api.get("/api/concierge/stats").then(unwrap),
};

export const submissionsApi = {
  submitMix: (payload) => api.post("/api/submissions/mix", payload).then(unwrap),
  listMixes: (params = {}) => api.get("/api/submissions/mix", { params }).then(unwrap),
  updateMix: (id, payload) => api.put(`/api/submissions/mix/${id}`, payload).then(unwrap),
  submitRosterApplication: (payload) =>
    api.post("/api/submissions/roster-application", payload).then(unwrap),
  listRosterApplications: (params = {}) =>
    api.get("/api/submissions/roster-application", { params }).then(unwrap),
  updateRosterApplication: (id, payload) =>
    api.put(`/api/submissions/roster-application/${id}`, payload).then(unwrap),
};

export const affiliateExternalApi = {
  getStats: () => api.get("/api/affiliate/external-stats").then(unwrap),
  getAllStats: () => api.get("/api/affiliate/external-stats/all").then(unwrap),
};
